import { DragDropContext, type DropResult, type OnDragEndResponder } from '@hello-pangea/dnd';
import { ErrorBoundary } from '@src/components/ErrorBoundary';
import { Loader, LoaderWrapper } from '@src/components/Loader';
import type { PredictionMarketCardConfig } from '@talos/kyoko';
import {
  EmptyMessage,
  EmptyView,
  Icon,
  IconName,
  MarketDataCardPlaceholderWrapper,
  usePageVisibility,
  useSecuritiesContext,
} from '@talos/kyoko';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePredictionsContext } from '../../providers/PredictionsProvider';
import { usePanelContext } from '../Layout/providers/PanelContextProvider';
import { PredictionMarketDroppableContainer } from './PredictionMarketDroppableContainer';
import { AddButton, Cards, Column, PredictionMarketsWrapper } from './styles';
import { usePredictionMarketCardColumns } from './usePredictionMarketCardColumns';

export interface PredictionMarketCardsProps {
  columnsTabID: string | undefined;
  columns: PredictionMarketCardConfig[][];
  createCard: (columnIndex: number) => void;
  moveCard: (event: DropResult<string>) => void;
  cardFilter: string;
  isAddingCard: boolean;
  sortCards: boolean;
  isShowingPlaceholders: boolean;
  selectedTabID: string;
  onToggleAddingCard: () => void;
  deleteCard: (card: PredictionMarketCardConfig) => void;
  saveCard: (card: PredictionMarketCardConfig) => void;
  hidePlaceholders: () => void;
}

export function PredictionMarketCards({
  columnsTabID,
  columns,
  createCard,
  moveCard,
  deleteCard,
  saveCard,
  hidePlaceholders,
  cardFilter,
  sortCards,
  isAddingCard,
  isShowingPlaceholders,
  selectedTabID,
  onToggleAddingCard,
}: PredictionMarketCardsProps) {
  const isVisible = usePageVisibility();
  const { isPopout } = usePanelContext();
  const [renderCards, setRenderCards] = useState(true);
  const { predictionOutcomesBySymbol } = useSecuritiesContext();
  const { refDataIndexes, isRefDataReady } = usePredictionsContext();
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showEmptyColumns, setShowEmptyColumns] = useState(false);

  useEffect(() => {
    if (isVisible || isPopout) {
      setRenderCards(true);
      if (hideTimer.current != null) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
    } else if (hideTimer.current == null && !isPopout) {
      hideTimer.current = setTimeout(() => {
        setRenderCards(false);
      }, 5000);
    }
  }, [isPopout, isVisible]);

  const lastColumnIndex = columns.reduceRight(
    (result, column, index) => (column.length > 0 ? Math.max(result, index) : result),
    -1
  );
  const columnsToRender = usePredictionMarketCardColumns(
    sortCards,
    isAddingCard,
    columns,
    predictionOutcomesBySymbol,
    refDataIndexes,
    isRefDataReady
  );
  const isFiltering = cardFilter !== '';

  const handleInitDrag = useCallback(() => setShowEmptyColumns(true), []);
  const handleEndDrag = useCallback<OnDragEndResponder<string>>(
    event => {
      setShowEmptyColumns(false);
      moveCard(event);
    },
    [moveCard]
  );

  if (columns == null) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  const hasNoColumns = columns.every(column => column.length === 0);

  return (
    <PredictionMarketsWrapper>
      <ErrorBoundary>
        <Cards isEmptyView={hasNoColumns && !isAddingCard}>
          {hasNoColumns && !isAddingCard
            ? selectedTabID === columnsTabID && (
                <EmptyView>
                  <AddButton onClick={onToggleAddingCard}>
                    <EmptyMessage>No prediction cards have been set up yet</EmptyMessage>
                    <Icon icon={IconName.Calendar} size={48} spaceBelow="spacing16" />
                    Add your first prediction card
                  </AddButton>
                </EmptyView>
              )
            : renderCards && (
                <DragDropContext onBeforeCapture={handleInitDrag} onDragEnd={handleEndDrag}>
                  {columnsToRender.map((cards, index) => (
                    <Column key={index}>
                      {isShowingPlaceholders && (
                        <MarketDataCardPlaceholderWrapper
                          onClick={() => createCard(index)}
                          data-testid="prediction-market-card-placeholder"
                        >
                          <Icon icon={IconName.Calendar} size={48} />
                        </MarketDataCardPlaceholderWrapper>
                      )}
                      <PredictionMarketDroppableContainer
                        columnIndex={index}
                        disableDrag={sortCards || isFiltering}
                        lastColumnIndex={showEmptyColumns ? columns.length : lastColumnIndex}
                        cards={cards}
                        cardFilter={cardFilter}
                        deleteCard={deleteCard}
                        saveCard={saveCard}
                        hidePlaceholders={hidePlaceholders}
                      />
                    </Column>
                  ))}
                </DragDropContext>
              )}
        </Cards>
      </ErrorBoundary>
    </PredictionMarketsWrapper>
  );
}
